import React, { useState, useEffect } from "react";
import { Highlight } from "../../model/Highlight";
import { API_URLS } from "../../App";
import api from "../../hooks/api";
import { Upload, X } from "lucide-react";

interface ManageHighlightProps {
  highlight?: Highlight;
  onHighlightUpdate: (highlight: Highlight) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

const ManageHighlight: React.FC<ManageHighlightProps> = ({
  highlight,
  onHighlightUpdate,
  setError,
  setSuccess,
}) => {
  const [formData, setFormData] = useState<Partial<Highlight>>({
    title: "",
    description: "",
    images: [],
    compound_id: "",
    link_url: "",
    begin_date: new Date().toISOString().slice(0, 16),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
    is_active: true,
    display_order: 0,
  });

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (highlight) {
      setFormData({
        ...highlight,
        images: highlight.images || [],
        begin_date: new Date(highlight.begin_date).toISOString().slice(0, 16),
        end_date: new Date(highlight.end_date).toISOString().slice(0, 16),
      });
    }
  }, [highlight]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const currentImages = formData.images || [];
    if (currentImages.length + files.length > 5) {
      setError("Máximo de 5 imagens permitidas");
      return;
    }

    setUploading(true);
    const uploadedUrls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);

        const response = await api.post(
          `${API_URLS.HIGHLIGHTS}/upload-image`,
          formDataUpload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        uploadedUrls.push(response.data.image_url);
      }

      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...uploadedUrls],
      }));
    } catch (error: any) {
      console.error("Error uploading images:", error);
      setError(`Erro ao fazer upload das imagens: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title?.trim()) {
      setError("O título é obrigatório");
      return;
    }

    if (!formData.begin_date || !formData.end_date) {
      setError("As datas de início e fim são obrigatórias");
      return;
    }

    const beginDate = new Date(formData.begin_date);
    const endDate = new Date(formData.end_date);

    if (beginDate > endDate) {
      setError("A data de início deve ser anterior à data de fim");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const dataToSave: Partial<Highlight> = {
        ...formData,
        begin_date: new Date(formData.begin_date!).toISOString(),
        end_date: new Date(formData.end_date!).toISOString(),
      };

      let response;
      if (highlight?.id) {
        // Update existing
        response = await api.put(
          `${API_URLS.HIGHLIGHTS}/${highlight.id}`,
          dataToSave,
        );
      } else {
        // Create new
        response = await api.post(API_URLS.HIGHLIGHTS, dataToSave);
      }

      onHighlightUpdate(response.data);
    } catch (error: any) {
      console.error("Error saving highlight:", error);
      setError(
        `Erro ao guardar destaque: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const remainingSlots = 5 - (formData.images?.length || 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Título *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Título do destaque"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Descrição
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Descrição detalhada do destaque"
        />
      </div>

      {/* Multiple Images Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Imagens (máximo 5)
        </label>

        {/* Current Images */}
        {formData.images && formData.images.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            {formData.images.map((url, index) => (
              <div key={index} className="relative">
                <img
                  src={url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-32 object-cover rounded border"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload Button */}
        {remainingSlots > 0 && (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {uploading
                  ? "A carregar..."
                  : `Clique para adicionar imagens (${remainingSlots} restantes)`}
              </p>
              <p className="text-xs text-gray-400">PNG, JPG, GIF (máx. 10MB)</p>
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={(e) => handleImageUpload(e.target.files)}
            />
          </label>
        )}
      </div>

      {/* Compound ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          ID do Composto (Produto)
        </label>
        <input
          type="text"
          name="compound_id"
          value={formData.compound_id}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="ID do produto relacionado (opcional)"
        />
        <p className="text-xs text-gray-500 mt-1">
          Deixe vazio se não quiser vincular a um produto
        </p>
      </div>

      {/* Link URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Link Externo
        </label>
        <input
          type="url"
          name="link_url"
          value={formData.link_url}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="https://exemplo.com (opcional)"
        />
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data de Início *
          </label>
          <input
            type="datetime-local"
            name="begin_date"
            value={formData.begin_date}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data de Fim *
          </label>
          <input
            type="datetime-local"
            name="end_date"
            value={formData.end_date}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Display Order and Active */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ordem de Exibição
          </label>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
          />
          <p className="text-xs text-gray-500 mt-1">
            Menor número aparece primeiro
          </p>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Ativo</span>
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <button
          type="submit"
          disabled={saving || uploading}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
        >
          {saving
            ? "A guardar..."
            : uploading
              ? "A carregar imagens..."
              : highlight?.id
                ? "Atualizar"
                : "Criar"}
        </button>
      </div>
    </form>
  );
};

export default ManageHighlight;
